import React, { ForwardedRef, forwardRef, memo } from 'react'
import { Collapse, FilledInputProps, InputAdornment, InputBaseComponentProps, InputProps, OutlinedInputProps } from '@mui/material'
import { FormValueInput, FormValueMeta } from 'types'
import { getShortName } from 'services/form'
import ThemedMUITextField, { ThemedMUITextFieldProps } from '../ThemedInputBase'

export interface ITextFieldProps extends ThemedMUITextFieldProps {
  name?: string
  input?: FormValueInput<string>
  meta?: FormValueMeta
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode

  /** @deprecated */
  'data-cy'?: string
  'data-testid'?: string
}

function TextField(props: ITextFieldProps, ref: ForwardedRef<HTMLInputElement>) {
  const {
    input,
    meta = {} as FormValueMeta,
    onChange,
    onBlur,
    name,
    value,
    label,
    helperText,
    startAdornment,
    endAdornment,
    error,
    ...restProps
  } = props

  const actualValue = input ? input.value : value
  // TODO: investigate using startTransition on the change/blur callbacks in this and other primitive components
  // See: https://beta.reactjs.org/reference/react/useTransition#updating-an-input-in-a-transition-doesnt-work
  const actualOnChange = input ? input.onChange : onChange
  const actualOnBlur = input ? input.onBlur : onBlur
  const errorText = meta.touched ? meta.error : undefined
  const hasError = error || !!errorText
  const actualHelperText = errorText ?? helperText
  const shortName = getShortName(input?.name || name || '')
  const obfuscate = shortName?.indexOf('password') || restProps?.type === 'password' // needed for analytics

  let inputPropsInner = {} as InputBaseComponentProps

  if (shortName) {
    inputPropsInner['data-cy'] = `textfield-input-${shortName}`
    inputPropsInner['data-testid'] = `textfield-input-${shortName}`
  }

  if (obfuscate) {
    inputPropsInner['data-private'] = true
  } else {
    inputPropsInner['data-public'] = true
  }

  if (props.inputProps) {
    inputPropsInner = {
      ...inputPropsInner,
      ...props.inputProps,
    }
  }

  let InputPropsInner: Partial<InputProps> | Partial<FilledInputProps> | Partial<OutlinedInputProps> = {
    startAdornment: startAdornment && (<InputAdornment position="start">{startAdornment}</InputAdornment>),
    endAdornment: endAdornment && (<InputAdornment position="end">{endAdornment}</InputAdornment>),
  }

  if (props.InputProps) {
    InputPropsInner = {
      ...InputPropsInner,
      ...props.InputProps,
    }
  }

  return (
    <ThemedMUITextField
      data-cy={shortName ? `textfield-${shortName}` : undefined}
      data-testid={shortName ? `textfield-${shortName}` : undefined}
      {...restProps}
      inputRef={ref}
      value={actualValue || ''} // avoid passing undefined, causing uncontrolled/controlled component warning from React
      onChange={actualOnChange}
      onBlur={actualOnBlur}
      variant="outlined"
      label={label}
      margin="dense"
      size="small"
      multiline={false}
      error={hasError}
      helperText={(
        <Collapse
          in={!!actualHelperText}
          data-cy={shortName ? `textfield-helper-${shortName}` : undefined}
          data-testid={shortName ? `textfield-helper-${shortName}` : undefined}
        >
          {actualHelperText}{'\u00A0'}
        </Collapse>
      )}
      inputProps={inputPropsInner}
      InputProps={InputPropsInner}
      FormHelperTextProps={{
        style: actualHelperText ? undefined : { marginTop: 0 }, // remove incorrect +4px spacing from the presence of empty helperText Collapse
        component: 'div', // default is 'p', this fixes invalid HTML by having the Collapse DIV in here
        // TODO: fix usage of 'any' here
      } as any}
    />
  )
}

export default memo(forwardRef(TextField))
